import { tier } from "../game/tiers";
import { shade } from "./colors";

/**
 * Per-tier celestial SVG art (spec §4). Each tile is drawn as a distinct space
 * object using only SVG shapes + gradients/filters from buildDefs() — no raster
 * assets. Coordinates are centered on (0,0); the tile group is translated to the
 * cell center by the renderer. Reference radius ≈ 42.
 */

function rays(color: string): string {
  let out = "";
  for (let i = 0; i < 12; i++) {
    out += `<line x1="0" y1="-24" x2="0" y2="-46" stroke="${color}" stroke-width="3.4" stroke-linecap="round" transform="rotate(${i * 30})" opacity="0.75"/>`;
  }
  return `<g class="cm-spin-med">${out}</g>`;
}

function spiralDots(): string {
  let out = "";
  for (let arm = 0; arm < 2; arm++) {
    const base = arm * Math.PI;
    for (let t = 0; t < 13; t++) {
      const ang = base + t * 0.52;
      const rad = 7 + t * 3.0;
      const x = (rad * Math.cos(ang)).toFixed(1);
      const y = (rad * Math.sin(ang)).toFixed(1);
      const s = (3.4 - t * 0.13).toFixed(1);
      const op = (0.95 - t * 0.05).toFixed(2);
      const col = t < 4 ? "#fff7ff" : t < 9 ? "#cdb6ff" : "#7b6cff";
      out += `<circle cx="${x}" cy="${y}" r="${s}" fill="${col}" opacity="${op}"/>`;
    }
  }
  return out;
}

function sparkles(): string {
  const pts = [
    [-22, -14, 0],
    [18, -20, 0.4],
    [26, 8, 0.8],
    [-12, 22, 1.1],
    [6, 28, 0.2],
    [-28, 6, 0.6],
    [14, 18, 1.0],
    [0, -28, 0.9],
  ];
  return pts
    .map(
      ([x, y, d]) =>
        `<g class="cm-twinkle" style="animation-delay:${d}s" transform="translate(${x} ${y})">
          <path d="M0,-5 L1,-1 L5,0 L1,1 L0,5 L-1,1 L-5,0 L-1,-1 Z" fill="#fff"/>
        </g>`,
    )
    .join("");
}

/** Special-tile SVG: wild = rainbow swirl, bomb = pulsing red orb with fuse. */
function specialArtSvg(kind: "wild" | "bomb"): string {
  if (kind === "wild") {
    // Rainbow conic-ish swirl using multiple offset arcs + a bright core.
    return `
      <circle r="42" fill="url(#halo)" opacity="0.4" class="cm-pulse"/>
      <g class="cm-spin">
        <circle r="32" fill="none" stroke="#ff5fd0" stroke-width="6" stroke-dasharray="40 25"/>
        <circle r="26" fill="none" stroke="#3fd6e8" stroke-width="6" stroke-dasharray="35 22" transform="rotate(70)"/>
        <circle r="20" fill="none" stroke="#ffd54a" stroke-width="6" stroke-dasharray="30 18" transform="rotate(140)"/>
      </g>
      <circle r="14" fill="#ffffff"/>
      <circle r="6" fill="#ff5fd0"/>
      <circle cx="-2" cy="-3" r="3" fill="#ffffff"/>`;
  }
  // bomb
  return `
    <circle r="42" fill="url(#halo)" opacity="0.55" class="cm-pulse-strong"/>
    <circle r="32" fill="#1a0306"/>
    <circle r="32" fill="none" stroke="#ff3b3b" stroke-width="2.5" opacity="0.85"/>
    <circle r="28" fill="#2a0a0c"/>
    <g class="cm-pulse-strong">
      <circle r="18" fill="#ff3b3b" opacity="0.55"/>
      <circle r="10" fill="#ffaa3b"/>
      <circle r="4" fill="#ffffff"/>
    </g>
    <path d="M 22 -22 Q 32 -32 36 -28" fill="none" stroke="#cfd3da" stroke-width="3" stroke-linecap="round"/>
    <circle cx="37" cy="-27" r="3.5" fill="#fff7a8" class="cm-twinkle"/>
    <circle cx="37" cy="-27" r="1.5" fill="#ffffff"/>`;
}

/**
 * Boss tile: a dark obsidian crystal with an ominous core. Crack lines appear
 * as it takes damage (more cracks at lower HP). `hp` is remaining hit points.
 */
function bossArtSvg(hp: number): string {
  // Jagged crystal silhouette.
  const shape =
    "M 0 -38 L 26 -18 L 34 10 L 16 36 L -16 36 L -34 10 L -26 -18 Z";
  // Cracks revealed progressively as HP drops (hp 3+: none, 2: one, 1: two).
  const cracks =
    hp <= 2
      ? `<path d="M -8 -20 L 2 -4 L -6 12" fill="none" stroke="#ff5fd0" stroke-width="2" opacity="0.8"/>` +
        (hp <= 1
          ? `<path d="M 14 -10 L 6 6 L 16 22" fill="none" stroke="#ff8a3b" stroke-width="2" opacity="0.85"/>`
          : "")
      : "";
  return `
    <circle r="44" fill="url(#halo)" opacity="0.18" class="cm-pulse"/>
    <path d="${shape}" fill="#15102a" stroke="#5b2a86" stroke-width="2.5"/>
    <path d="${shape}" fill="url(#halo)" opacity="0.08"/>
    <path d="M 0 -38 L 26 -18 L 0 -6 Z" fill="#2a1a4a" opacity="0.8"/>
    <path d="M -26 -18 L 0 -6 L 0 -38 Z" fill="#1d1338" opacity="0.8"/>
    <circle r="10" fill="#9a3bff" opacity="0.9" class="cm-pulse-strong"/>
    <circle r="4" fill="#e6d6ff"/>
    ${cracks}`;
}

/** Inner SVG markup for a tile of the given tier level. */
export function tileArtSvg(level: number): string {
  if (level === -1) return specialArtSvg("wild");
  if (level === -2) return specialArtSvg("bomb");
  if (level <= -100) return bossArtSvg(-100 - level);
  const tr = tier(level);
  const c = tr.color;
  const dark = shade(c, -0.5);
  const light = shade(c, 0.6);
  const g = `url(#grad-${level})`;

  switch (tr.key) {
    case "particle":
      // A glowing pearl: soft outer aura, gradient body, sharp highlight,
      // and three tiny orbiting motes for life. Reads cleanly at any size.
      return `
        <circle r="42" fill="url(#halo)" opacity="0.45" class="cm-pulse"/>
        <circle r="22" fill="${g}" stroke="${light}" stroke-width="1.5" stroke-opacity="0.7"/>
        <ellipse rx="9" ry="6" cx="-6" cy="-8" fill="#ffffff" opacity="0.85"/>
        <circle cx="-6" cy="-8" r="2.5" fill="#ffffff"/>
        <g class="cm-spin-med">
          <circle cx="28" cy="0" r="2" fill="${light}" opacity="0.9"/>
          <circle cx="-26" cy="6" r="1.6" fill="#ffffff" opacity="0.85"/>
          <circle cx="6" cy="-30" r="1.6" fill="${light}" opacity="0.8"/>
        </g>`;

    case "dust":
      // A turbulent dust cloud: layered translucent puffs that drift in
      // opposite directions, plus a few bright micro-dust specks.
      return `
        <g class="cm-spin-slow">
          <circle cx="-16" cy="-6" r="18" fill="${g}" opacity="0.55"/>
          <circle cx="10" cy="-14" r="14" fill="${light}" opacity="0.45"/>
          <circle cx="16" cy="10" r="20" fill="${g}" opacity="0.55"/>
          <circle cx="-12" cy="14" r="16" fill="${c}" opacity="0.55"/>
          <circle cx="0" cy="0" r="12" fill="${light}" opacity="0.65"/>
        </g>
        <g class="cm-spin-med" style="animation-direction: reverse">
          <circle cx="22" cy="-8" r="2" fill="#ffffff" opacity="0.95"/>
          <circle cx="-18" cy="14" r="1.6" fill="#ffffff" opacity="0.85"/>
          <circle cx="6" cy="-20" r="1.4" fill="${light}"/>
          <circle cx="-22" cy="-12" r="1.4" fill="${light}" opacity="0.8"/>
          <circle cx="14" cy="18" r="1.2" fill="#ffffff" opacity="0.9"/>
        </g>`;

    case "asteroid": {
      // An irregular rocky shape (not a perfect circle), with banded shading
      // for depth and a couple of crisp crater rings.
      // Polygon points are pre-baked to a "lumpy potato" silhouette.
      const path =
        "M -34 -8 L -28 -22 L -10 -32 L 8 -34 L 26 -24 L 36 -8 " +
        "L 34 10 L 22 28 L 4 34 L -16 30 L -30 18 L -38 4 Z";
      return `
        <path d="${path}" fill="${g}" stroke="${dark}" stroke-width="1.5"/>
        <path d="${path}" fill="url(#halo)" opacity="0.18"/>
        <path d="M -28 -10 L -8 -28 L 24 -22 L 30 -4"
              fill="none" stroke="${light}" stroke-width="2" opacity="0.45"/>
        <g>
          <circle cx="-10" cy="-6" r="6" fill="${dark}" opacity="0.85"/>
          <circle cx="-10" cy="-6" r="6" fill="none" stroke="${light}" stroke-width="0.8" opacity="0.6"/>
          <circle cx="12" cy="8" r="5" fill="${dark}" opacity="0.85"/>
          <circle cx="12" cy="8" r="5" fill="none" stroke="${light}" stroke-width="0.7" opacity="0.5"/>
          <circle cx="6" cy="-16" r="3" fill="${dark}" opacity="0.75"/>
          <circle cx="-18" cy="14" r="2.5" fill="${dark}" opacity="0.7"/>
        </g>`;
    }

    case "moon":
      // Full moon with a crescent terminator (sun from the upper-left),
      // a few crater rings, and a faint outer glow.
      return `
        <circle r="42" fill="url(#halo)" opacity="0.25"/>
        <circle r="38" fill="${g}"/>
        <path d="M 0 -38 A 38 38 0 1 1 0 38 A 26 38 0 1 0 0 -38 Z"
              fill="${dark}" opacity="0.32"/>
        <circle cx="-14" cy="-12" r="7" fill="${dark}" opacity="0.55"/>
        <circle cx="-14" cy="-12" r="7" fill="none" stroke="${light}" stroke-width="0.9" opacity="0.55"/>
        <circle cx="12" cy="4" r="5" fill="${dark}" opacity="0.5"/>
        <circle cx="12" cy="4" r="5" fill="none" stroke="${light}" stroke-width="0.8" opacity="0.45"/>
        <circle cx="2" cy="16" r="4" fill="${dark}" opacity="0.45"/>
        <circle cx="-4" cy="-20" r="2.5" fill="${dark}" opacity="0.5"/>
        <circle cx="18" cy="-12" r="2" fill="${dark}" opacity="0.4"/>
        <ellipse rx="14" ry="6" cx="-10" cy="-22" fill="#ffffff" opacity="0.18"/>`;

    case "planet":
      return `
        <g class="cm-spin">
          <circle r="34" fill="${g}"/>
          <ellipse rx="32" ry="7" cy="-9" fill="${light}" opacity="0.25"/>
          <ellipse rx="30" ry="6" cy="9" fill="${dark}" opacity="0.3"/>
          <ellipse rx="20" ry="4" cy="-2" fill="${light}" opacity="0.2"/>
        </g>
        <ellipse rx="48" ry="15" fill="none" stroke="#cfe0ff" stroke-width="3.5" opacity="0.85" transform="rotate(-20)"/>`;

    case "star":
      return `
        ${rays(shade(c, 0.4))}
        <circle r="40" fill="url(#core-white)" opacity="0.6" class="cm-pulse"/>
        <circle r="22" fill="${g}" class="cm-pulse"/>`;

    case "redGiant":
      return `
        <circle r="46" fill="${g}" opacity="0.3" class="cm-pulse-strong"/>
        <circle r="38" fill="${g}" class="cm-pulse-strong"/>
        <circle r="26" fill="${light}" opacity="0.4" class="cm-pulse"/>`;

    case "neutronStar":
      return `
        <g class="cm-spin-fast">
          <rect x="-3.5" y="-50" width="7" height="100" fill="url(#beam)"/>
          <rect x="-50" y="-3.5" width="100" height="7" fill="url(#beam)" opacity="0.5"/>
        </g>
        <circle r="16" fill="url(#core-white)"/>
        <circle r="8" fill="#e6fdff"/>`;

    case "blackHole":
      return `
        <g class="cm-spin">
          <circle r="46" fill="url(#accretion)"/>
        </g>
        <circle r="21" fill="#05050a"/>
        <circle r="22" fill="none" stroke="#9a6bff" stroke-width="1.5" opacity="0.6"/>`;

    case "quasar":
      return `
        <g class="cm-spin-slow">
          <rect x="-4" y="-54" width="8" height="108" fill="url(#beam)" opacity="0.9"/>
        </g>
        <circle r="32" fill="${g}" opacity="0.45" class="cm-pulse"/>
        <circle r="17" fill="url(#core-white)"/>`;

    case "galaxy":
      return `
        <g class="cm-spin-med">
          ${spiralDots()}
        </g>
        <circle r="9" fill="url(#core-white)"/>`;

    case "cosmos":
      return `
        <circle r="42" fill="url(#nebula)"/>
        ${sparkles()}
        <circle r="42" fill="url(#halo)" opacity="0.18"/>`;

    default:
      return `<circle r="40" fill="${g}"/>`;
  }
}

/** Tiers whose art warrants the stronger glow filter on the whole tile. */
export function tileGlowFilter(level: number): string {
  if (level < 0) return "url(#tile-glow)"; // specials always glow softly
  const t = tier(level);
  if (!t.glow) return "";
  return level >= 6 ? "url(#tile-glow-strong)" : "url(#tile-glow)";
}
